/**
 * Trade Categories Configuration
 *
 * SINGLE SOURCE OF TRUTH for all tradesperson categories in the system.
 * This file defines all supported trade types, their display names,
 * icons, and keyword mappings.
 */

// Lucide icons
import {
  Wrench,
  Hammer,
  PaintBucket,
  Scissors,
  Shovel,
  Lightbulb,
  TreePine,
  Flame,
  Home,
  Droplets,
  Wind,
  Ruler,
  ArrowRight,
  Construction,
  Paintbrush,
  Bug,
  KeyRound,
  Bath,
  Layers,
  Droplet,
  CircleOff,
  BrickWall,
  Sun,
  AppWindow,
  Sparkles,
  LucideIcon
} from 'lucide-react';

// Icon type is now just LucideIcon
type IconComponent = LucideIcon;

/**
 * Trade category definition
 */
export interface TradeCategory {
  /** Internal identifier (used in database) */
  id: string;

  /** User-facing display name */
  displayName: string;

  /** Icon component to represent this trade */
  icon: IconComponent;

  /** Keywords associated with this trade (for search and smart categorization) */
  keywords: string[];

  /** UI category for grouping similar trades (used in TradesPurple) */
  uiCategory?: string;

  /** Whether this is a legacy category that should be migrated */
  isLegacy?: boolean;
}

/**
 * Complete list of all trade categories
 */
export const TRADE_CATEGORIES: TradeCategory[] = [
  // Plumbing category
  {
    id: 'plumber',
    displayName: 'Plumber',
    icon: Droplets,
    uiCategory: 'plumbing',
    keywords: [
      'tap', 'taps', 'pipe', 'pipes', 'toilet', 'loo', 'shower', 'sink', 'drain',
      'faucet', 'plumber', 'plumbing', 'water leak', 'blocked drain', 'toilet',
      'flooding', 'u bend', 'stopcock', 'cistern', 'bidet', 'basin'
    ]
  },

  // Heating category
  {
    id: 'heating engineer',
    displayName: 'Heating Engineer',
    icon: Flame,
    uiCategory: 'heating',
    keywords: [
      'Boiler', 'boilers', 'radiator', 'radiators', 'thermostat', 'combi',
      'vaillant', 'worcester', 'hot water', 'bleed', 'expansion cessel',
      'gas', 'furnace', 'central heating'
    ]
  },

  // Electrical category
  {
    id: 'electrician',
    displayName: 'Electrician',
    icon: Lightbulb,
    uiCategory: 'electrical',
    keywords: [
      'socket', 'switch', 'electrician', 'fuse', 'wiring', 'wire', 'RCD',
      'breaker', 'consumer unit', 'rewire', 'EV charger', 'electrical',
      'power', 'circuit'
    ]
  },

  // Building-related trades
  {
    id: 'builder',
    displayName: 'Builder',
    icon: BrickWall,
    uiCategory: 'building',
    keywords: [
      'extension', 'brick', 'knock through', 'renovation', 'construction',
      'foundations', 'mortar'
    ]
  },

  {
    id: 'plasterer',
    displayName: 'Plasterer',
    icon: Construction,
    uiCategory: 'building',
    keywords: [
      'plaster', 'skim', 'render', 'artex', 'plasterer', 'plastering',
      'skimming', 'drywall'
    ]
  },

  {
    id: 'tiler',
    displayName: 'Tiler',
    icon: Layers,
    uiCategory: 'building',
    keywords: [
      'tiles', 'tiling', 'tiler'
    ]
  },

  // Roofing category
  {
    id: 'roofer',
    displayName: 'Roofer',
    icon: Home,
    uiCategory: 'roofing',
    keywords: [
      'roof', 'roofing', 'gutter', 'gutters', 'downpipe', 'fascia', 'soffit',
      'ridge', 'valley', 'flashing', 'shingle'
    ]
  },

  // Cleaning & maintenance
  {
    id: 'chimney sweep',
    displayName: 'Chimney Sweep',
    icon: Wind,
    uiCategory: 'building',
    keywords: [
      'chimney', 'chimney sweep', 'fireplace', 'flue', 'soot', 'stove',
      'log burner', 'logburner', 'hearth'
    ]
  },

  {
    id: 'window cleaner',
    displayName: 'Window Cleaner',
    icon: AppWindow,
    uiCategory: 'cleaning',
    keywords: [
      'clean window', 'dirty window', 'clean windows', 'window cleaner'
    ]
  },

  {
    id: 'cleaner',
    displayName: 'Cleaner',
    icon: Sparkles,
    uiCategory: 'cleaning',
    keywords: [
      'cleaner', 'cleaning', 'housekeeping', 'housekeeper', 'domestic',
      'maid', 'tidy', 'vacuum', 'dust', 'sanitize', 'disinfect',
      'deep clean', 'end of tenancy', 'office cleaning', 'commercial cleaning'
    ]
  },

  // Decoration & Handyperson combined
  {
    id: 'decorator',
    displayName: 'Handyman',
    icon: PaintBucket,
    uiCategory: 'decoration',
    keywords: [
      'decorator', 'decoration', 'painting', 'decorating', 'wallpaper',
      'odd job', 'handyman', 'assembly', 'handyperson'
    ]
  },

  // Windows & doors
  {
    id: 'glazier',
    displayName: 'Glazier',
    icon: Ruler,
    uiCategory: 'building',
    keywords: [
      'glass', 'glazing', 'glazier', 'glass door', 'pane'
    ]
  },

  // Carpentry
  {
    id: 'carpenter',
    displayName: 'Carpenter',
    icon: Hammer,
    uiCategory: 'building',
    keywords: [
      'carpenter', 'joinery', 'woodwork'
    ]
  },

  // Garden related
  {
    id: 'gardener',
    displayName: 'Gardener',
    icon: Shovel,
    uiCategory: 'gardening',
    keywords: [
      'lawn', 'grass', 'weed', 'mulch', 'strim', 'patio', 'landscape',
      'plant', 'flower', 'prune', 'shrub', 'garden', 'hedge'
    ]
  },

  {
    id: 'tree surgeon',
    displayName: 'Tree Surgeon',
    icon: TreePine,
    uiCategory: 'gardening',
    keywords: [
      'tree', 'tree removal', 'stump', 'chainsaw', 'crown'
    ]
  },

  {
    id: 'fencing contractor',
    displayName: 'Fencing Contractor',
    icon: Construction,
    uiCategory: 'gardening',
    keywords: [
      'fence', 'fencing', 'gate', 'panel'
    ]
  },

  // Specialized trades
  {
    id: 'solar specialist',
    displayName: 'Solar Specialist',
    icon: Sun,
    uiCategory: 'electrical',
    keywords: [
      'solar panel', 'PV', 'battery', 'inverter'
    ]
  },

  {
    id: 'pest control',
    displayName: 'Pest Control',
    icon: Bug,
    uiCategory: 'building',
    keywords: [
      'rat', 'mouse', 'insect', 'wasp', 'bee', 'ant', 'cockroach',
      'vermin', 'infest', 'nest'
    ]
  },

  {
    id: 'locksmith',
    displayName: 'Locksmith',
    icon: KeyRound,
    uiCategory: 'building',
    keywords: [
      'lock', 'yale', 'deadlock'
    ]
  },

  {
    id: 'kitchen bathroom fitter',
    displayName: 'Kitchen & Bathroom Fitter',
    icon: Bath,
    uiCategory: 'building',
    keywords: [
      'worktop', 'counter'
    ]
  },

  {
    id: 'insulation specialist',
    displayName: 'Insulation Specialist',
    icon: Layers,
    uiCategory: 'building',
    keywords: [
      'insulation'
    ]
  },

  {
    id: 'damp specialist',
    displayName: 'Damp Specialist',
    icon: Droplet,
    uiCategory: 'building',
    keywords: [
      'damp', 'mold'
    ]
  },

  {
    id: 'flooring specialist',
    displayName: 'Flooring Specialist',
    icon: Layers,
    uiCategory: 'building',
    keywords: [
      'flooring', 'laminate', 'parquet', 'lino', 'carpet'
    ]
  },

  // Other/General category
  {
    id: 'other',
    displayName: 'Other',
    icon: Wrench,
    uiCategory: 'other',
    keywords: [
      'other', 'general', 'misc', 'miscellaneous', 'various', 'different',
      'general contractor', 'contractor', 'handyperson', 'general maintenance'
    ]
  },

  // Legacy categories that should be phased out or migrated
  {
    id: 'painter',
    displayName: 'Painter',
    icon: Paintbrush,
    uiCategory: 'decoration',
    isLegacy: true,
    keywords: []
  }
];

/**
 * Mapping of common keywords to trade categories for searching and smart categorization
 */
export const KEYWORD_TO_TRADE_MAP: Record<string, string> = {};

// Build the keyword map automatically from the trade categories
TRADE_CATEGORIES.forEach(category => {
  category.keywords.forEach(keyword => {
    // Skip very generic keywords that might cause false matches
    if (keyword.length < 3) return;

    KEYWORD_TO_TRADE_MAP[keyword.toLowerCase()] = category.id;
  });
});

/**
 * Get a trade category by its ID
 */
export function getTradeCategory(id: string | null | undefined): TradeCategory | undefined {
  if (!id) return undefined;
  return TRADE_CATEGORIES.find(category => category.id.toLowerCase() === id.toLowerCase());
}

/**
 * Get the most likely trade category from job description text
 */
export function detectTradeCategory(text: string | null | undefined): TradeCategory | undefined {
  if (!text) return undefined;

  const normalizedText = text.toLowerCase();

  // Try to find keywords in the text
  for (const category of TRADE_CATEGORIES) {
    for (const keyword of category.keywords) {
      if (normalizedText.includes(keyword.toLowerCase())) {
        return category;
      }
    }
  }

  return undefined;
}

/**
 * Get trade category by UI category
 */
export function getTradesByUICategory(uiCategory: string): TradeCategory[] {
  return TRADE_CATEGORIES.filter(category =>
    category.uiCategory?.toLowerCase() === uiCategory.toLowerCase() && !category.isLegacy
  );
}

/**
 * Map UI category to trade ID for backwards compatibility
 */
export function mapUICategoryToTradeID(uiCategory: string): string | undefined {
  // Get the first non-legacy trade in the UI category
  const trade = TRADE_CATEGORIES.find(category =>
    category.uiCategory?.toLowerCase() === uiCategory.toLowerCase() && !category.isLegacy
  );

  return trade?.id;
}

/**
 * Get UI category for a trade ID
 */
export function getUICategoryFromTradeID(tradeID: string | null | undefined): string | undefined {
  if (!tradeID) return undefined;

  const trade = getTradeCategory(tradeID);
  return trade?.uiCategory;
}

/**
 * Map legacy category or type to standardized trade ID
 */
export function standardizeTradeType(type: string | null | undefined): string {
  if (!type) return 'other';

  const normalizedType = type.toLowerCase();

  // Try to match directly to a trade ID
  const exactMatch = TRADE_CATEGORIES.find(category =>
    category.id.toLowerCase() === normalizedType
  );

  if (exactMatch) return exactMatch.id;

  // Try to match by keywords
  for (const category of TRADE_CATEGORIES) {
    if (category.keywords.some(keyword => normalizedType.includes(keyword.toLowerCase()))) {
      return category.id;
    }
  }

  // Default to other if no match found
  return 'other';
}

/**
 * Get display name for a trade type
 */
export function getTradeDisplayName(tradeType: string | null | undefined): string {
  if (!tradeType) return 'Other';

  const category = getTradeCategory(tradeType);
  return category?.displayName || tradeType;
}

/**
 * Get icon for a trade type
 */
export function getTradeIcon(tradeType: string | null | undefined): IconComponent {
  if (!tradeType) return Wrench;

  const category = getTradeCategory(tradeType);
  return category?.icon || Wrench;
}

export default TRADE_CATEGORIES;
